import React, { useState, useEffect } from 'react';
import { Target, Plus, AlertCircle, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Wallet, Play, Check, Maximize2, X, ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useCategoryTranslation } from '../../hooks/useCategoryTranslation';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { useThemeStore } from '../../store/themeStore';
import type { BudgetProgressSummary, BudgetWithProgress, Category, WeeklyProgressItem, EnvelopeSummary, EnvelopeWithProgress } from '../../types';
import BudgetConfigModal from './BudgetConfigModal';
import EnvelopeConfigModal from './EnvelopeConfigModal';
import UpgradePrompt from '../common/UpgradePrompt';
import HelpTooltip from '../common/HelpTooltip';
import api from '../../services/api';

// Helper pour convertir kebab-case en PascalCase
function toPascalCase(str: string): string {
  return str
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

// Composant pour rendre une icône Lucide dynamiquement
function DynamicIcon({ name, className }: { name: string; className?: string }) {
  const pascalName = toPascalCase(name);
  const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[pascalName];

  if (!IconComponent) {
    return null;
  }

  return <IconComponent className={className} />;
}

interface BudgetProgressWidgetProps {
  type: 'shared' | 'personal';
  formatCurrency: (amount: number) => string;
  month?: number;
  year?: number;
  weekStart?: string;
  weekEnd?: string;
  expenseCategories: Category[];
  bankAccounts?: { id: string; name: string; color: string }[];
  filterBankAccountId?: string;
  carouselElement?: React.ReactNode;
}

function getStatusIcon(status: BudgetWithProgress['status']) {
  switch (status) {
    case 'on_track':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    case 'exceeded':
      return <AlertCircle className="w-4 h-4 text-red-500" />;
  }
}

function getStatusColor(status: BudgetWithProgress['status']) {
  switch (status) {
    case 'on_track':
      return 'bg-green-500';
    case 'warning':
      return 'bg-amber-500';
    case 'exceeded':
      return 'bg-red-500';
  }
}

function getStatusColorLight(status: WeeklyProgressItem['status']) {
  switch (status) {
    case 'on_track':
      return 'bg-green-400';
    case 'warning':
      return 'bg-amber-400';
    case 'exceeded':
      return 'bg-red-400';
  }
}

function getStatusTextColor(status: WeeklyProgressItem['status']) {
  switch (status) {
    case 'on_track':
      return 'text-green-600';
    case 'warning':
      return 'text-amber-600';
    case 'exceeded':
      return 'text-red-600';
  }
}

export default function BudgetProgressWidget({
  type,
  formatCurrency,
  month,
  year,
  weekStart,
  weekEnd,
  expenseCategories,
  bankAccounts,
  filterBankAccountId,
  carouselElement,
}: BudgetProgressWidgetProps) {
  const [summary, setSummary] = useState<BudgetProgressSummary | null>(null);
  const [envelopeSummary, setEnvelopeSummary] = useState<EnvelopeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [envelopeLoading, setEnvelopeLoading] = useState(true);
  // Défaut sur « Enveloppes » : premier onglet, cohérent avec l'ordre de la modale.
  const [activeTab, setActiveTab] = useState<'budgets' | 'envelopes'>('envelopes');
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isEnvelopeConfigOpen, setIsEnvelopeConfigOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<BudgetWithProgress | null>(null);
  const [editingEnvelope, setEditingEnvelope] = useState<EnvelopeWithProgress | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const themeId = useThemeStore((s) => s.themeId);
  const isMeridian = themeId === 'meridian';
  const { t } = useTranslation('dashboard');
  const { translateCategoryName } = useCategoryTranslation();
  const planInfo = useSubscriptionStore((s) => s.planInfo);
  const canCreateBudget = !planInfo || planInfo.limits.budgets === -1 || planInfo.usage.budgets < planInfo.limits.budgets;
  const canCreateEnvelope = !planInfo || planInfo.limits.envelopes === -1 || planInfo.usage.envelopes < planInfo.limits.envelopes;

  const getBudgetDisplayName = (budget: BudgetWithProgress): string => {
    if (budget.name === budget.subcategoryName && budget.expenseSubcategoryId) {
      return translateCategoryName(budget.expenseSubcategoryId, budget.name, budget.subcategoryIsDefault);
    }
    if (budget.name === budget.categoryName && budget.expenseCategoryId) {
      return translateCategoryName(budget.expenseCategoryId, budget.name, budget.categoryIsDefault);
    }
    return budget.name;
  };

  const [expandedBudgets, setExpandedBudgets] = useState<Set<string>>(new Set());

  const toggleBudgetExpand = (budgetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedBudgets(prev => {
      const next = new Set(prev);
      if (next.has(budgetId)) {
        next.delete(budgetId);
      } else {
        next.add(budgetId);
      }
      return next;
    });
  };

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('type', type);
      if (weekStart && weekEnd) {
        params.append('weekStart', weekStart);
        params.append('weekEnd', weekEnd);
      } else {
        if (month) params.append('month', month.toString());
        if (year) params.append('year', year.toString());
      }

      const response = await api.get(`/budgets/summary?${params.toString()}`);
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Erreur chargement budgets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnvelopes = async () => {
    try {
      setEnvelopeLoading(true);
      const params = new URLSearchParams();
      params.append('type', type);
      if (month) params.append('month', month.toString());
      if (year) params.append('year', year.toString());
      const response = await api.get(`/envelopes/summary?${params.toString()}`);
      if (response.data.success) {
        setEnvelopeSummary(response.data.data);
      }
    } catch (error) {
      console.error('Erreur chargement enveloppes:', error);
    } finally {
      setEnvelopeLoading(false);
    }
  };

  useEffect(() => {
    fetchBudgets();
    fetchEnvelopes();
  }, [type, month, year, weekStart, weekEnd]);

  // Overlay détail (Méridien) : fermeture au clavier + verrou du scroll de fond.
  useEffect(() => {
    if (!isDetailOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsDetailOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isDetailOpen]);

  const handleOpenConfig = () => {
    if (activeTab === 'envelopes') {
      setEditingEnvelope(null);
      setIsEnvelopeConfigOpen(true);
    } else {
      setEditingBudget(null);
      setIsConfigOpen(true);
    }
  };

  const handleEditBudget = (budget: BudgetWithProgress) => {
    setEditingBudget(budget);
    setIsConfigOpen(true);
  };

  const handleEditEnvelope = (envelope: EnvelopeWithProgress) => {
    setEditingEnvelope(envelope);
    setIsEnvelopeConfigOpen(true);
  };

  const handleCloseConfig = () => {
    setIsConfigOpen(false);
    setEditingBudget(null);
  };

  const handleCloseEnvelopeConfig = () => {
    setIsEnvelopeConfigOpen(false);
    setEditingEnvelope(null);
  };

  const fetchPlanInfo = useSubscriptionStore((s) => s.fetchPlanInfo);

  const handleSaveSuccess = () => {
    fetchBudgets();
    fetchPlanInfo();
    setIsConfigOpen(false);
    setEditingBudget(null);
  };

  const handleEnvelopeSaveSuccess = () => {
    fetchEnvelopes();
    fetchPlanInfo();
    setIsEnvelopeConfigOpen(false);
    setEditingEnvelope(null);
  };

  const handleConvertToSpending = async (envelopeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/envelopes/${envelopeId}/convert-to-spending`, {});
      fetchEnvelopes();
    } catch (error) {
      console.error('Erreur conversion:', error);
    }
  };

  const handleCompleteEnvelope = async (envelopeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/envelopes/${envelopeId}/complete`);
      fetchEnvelopes();
    } catch (error) {
      console.error('Erreur completion:', error);
    }
  };

  const isLoading = activeTab === 'budgets' ? loading : envelopeLoading;

  if (isLoading && (activeTab === 'budgets' ? !summary : !envelopeSummary)) {
    if (isMeridian) {
      return (
        <div className="pv">
          <div className="pv-head">
            <h3 className="pv-title">{t('budgetProgress.title')}</h3>
          </div>
          <div className="pv-list">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="animate-pulse"
                style={{ height: 46, margin: '8px 4px', borderRadius: 10, background: 'var(--panel-2)' }}
              />
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="card h-full animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-16 bg-gray-200 rounded"></div>
          <div className="h-16 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Filtrer les budgets par compte si un filtre est actif
  const filteredBudgets = summary ? (
    filterBankAccountId
      ? summary.budgets.filter(b =>
          !b.bankAccountIds || b.bankAccountIds.length === 0 || b.bankAccountIds.includes(filterBankAccountId)
        )
      : summary.budgets
  ) : [];

  // Filtrer les enveloppes par compte si un filtre est actif
  const filteredEnvelopes = envelopeSummary ? (
    filterBankAccountId
      ? envelopeSummary.envelopes.filter(e =>
          !e.bankAccountIds || e.bankAccountIds.length === 0 || e.bankAccountIds.includes(filterBankAccountId)
        )
      : envelopeSummary.envelopes
  ) : [];

  // Recalculer les totaux si filtré
  const displayTotalBudgeted = filteredBudgets.reduce((sum, b) => sum + b.amount, 0);
  const displayTotalSpent = filteredBudgets.reduce((sum, b) => sum + b.spent, 0);
  const displayTotalRemaining = displayTotalBudgeted - displayTotalSpent;

  const hasBudgets = filteredBudgets.length > 0;
  const hasEnvelopes = filteredEnvelopes.length > 0;

  const canCreate = activeTab === 'budgets' ? canCreateBudget : canCreateEnvelope;

  // Les deux modales de configuration, partagées par l'ancien rendu et la vue Méridien.
  const configModals = (
    <>
      {/* Modale unifiée — onglet Budget */}
      <BudgetConfigModal
        isOpen={isConfigOpen}
        onClose={handleCloseConfig}
        onSaveSuccess={handleSaveSuccess}
        type={type}
        editingBudget={editingBudget}
        expenseCategories={expenseCategories}
        bankAccounts={bankAccounts}
        month={month}
        year={year}
        onSwitchType={canCreateEnvelope ? () => {
          setEditingBudget(null);
          setIsConfigOpen(false);
          setEditingEnvelope(null);
          setIsEnvelopeConfigOpen(true);
        } : undefined}
      />

      {/* Modale unifiée — onglet Enveloppe */}
      <EnvelopeConfigModal
        isOpen={isEnvelopeConfigOpen}
        onClose={handleCloseEnvelopeConfig}
        onSaveSuccess={handleEnvelopeSaveSuccess}
        type={type}
        editingEnvelope={editingEnvelope}
        bankAccounts={bankAccounts}
        onSwitchType={canCreateBudget ? () => {
          setEditingEnvelope(null);
          setIsEnvelopeConfigOpen(false);
          setEditingBudget(null);
          setIsConfigOpen(true);
        } : undefined}
      />
    </>
  );

  // ============================================
  // Vue Méridien : widget compact (hauteur fixe, liste défilante)
  // + overlay détail au clic. Les autres thèmes gardent le rendu ci-dessous.
  // ============================================
  if (isMeridian) {
    const totalThisMonth = filteredEnvelopes.reduce((s, e) => s + (e.projectedThisMonth || 0), 0);
    const totalTarget = filteredEnvelopes.reduce((s, e) => s + e.targetAmount, 0);
    const openDetail = () => setIsDetailOpen(true);

    const emptyEnvelopes = (
      <div className="pv-empty">
        <Wallet />
        <p>{t('envelopeProgress.noEnvelopes')}</p>
        {canCreateEnvelope ? (
          <button className="pv-empty-btn" onClick={handleOpenConfig}>
            <Plus />{t('envelopeProgress.configureEnvelopes')}
          </button>
        ) : (
          <UpgradePrompt compact />
        )}
      </div>
    );
    const emptyBudgets = (
      <div className="pv-empty">
        <Target />
        <p>{t('budgetProgress.noBudgets')}</p>
        {canCreateBudget ? (
          <button className="pv-empty-btn" onClick={handleOpenConfig}>
            <Plus />{t('budgetProgressDetails.createBudget')}
          </button>
        ) : (
          <UpgradePrompt compact />
        )}
      </div>
    );

    return (
      <>
        <div className="pv">
          {carouselElement}
          <div className="pv-head">
            <div className="pv-lft">
              <h3 className="pv-title">{t('budgetProgress.title')}</h3>
              <HelpTooltip helpKey="budgetProgress" />
              <div className="segmented">
                <button
                  onClick={() => setActiveTab('envelopes')}
                  className={activeTab === 'envelopes' ? 'on' : undefined}
                >
                  {t('budgetProgress.envelopesTab')}
                </button>
                <button
                  onClick={() => setActiveTab('budgets')}
                  className={activeTab === 'budgets' ? 'on' : undefined}
                >
                  {t('budgetProgress.expensesTab')}
                </button>
              </div>
            </div>
            <div className="pv-tools">
              <button
                onClick={openDetail}
                className="icon-btn"
                title={t('budgetProgress.expand')}
                aria-label={t('budgetProgress.expand')}
              >
                <Maximize2 className="w-4 h-4" />
              </button>
              {canCreate && (
                <button
                  onClick={handleOpenConfig}
                  className="icon-btn"
                  style={{ color: 'var(--pa)', borderColor: 'var(--pa-soft)' }}
                  title={activeTab === 'budgets' ? t('budgetProgressDetails.addBudget') : t('envelopeConfig.newEnvelope')}
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {activeTab === 'envelopes' ? (
            hasEnvelopes ? (
              <>
                <div className="pv-sum">
                  <span>{t('envelopeProgress.savedThisMonth')} <b>{formatCurrency(totalThisMonth)}</b></span>
                  <span>{t('envelopeConfig.targetAmount')} <b>{formatCurrency(totalTarget)}</b></span>
                </div>
                <div className="pv-list">
                  {filteredEnvelopes.map((e) => (
                    <PvEnvelopeRow
                      key={e.id}
                      envelope={e}
                      formatCurrency={formatCurrency}
                      viewMonth={month}
                      viewYear={year}
                      onClick={openDetail}
                    />
                  ))}
                </div>
                <div className="pv-foot">
                  <button className="pv-see" onClick={openDetail}>
                    {t('budgetProgress.openDetail')} <ArrowRight />
                  </button>
                </div>
              </>
            ) : emptyEnvelopes
          ) : (
            hasBudgets ? (
              <>
                <div className="pv-sum">
                  <span>{t('budgetProgressDetails.spent')} <b>{formatCurrency(displayTotalSpent)}</b> / {formatCurrency(displayTotalBudgeted)}</span>
                  <span className={displayTotalRemaining >= 0 ? 'pos' : 'neg'}>
                    {t('budgetProgressDetails.remaining')} <b>{formatCurrency(displayTotalRemaining)}</b>
                  </span>
                </div>
                <div className="pv-list">
                  {filteredBudgets.map((b) => (
                    <PvBudgetRow
                      key={b.id}
                      budget={b}
                      name={getBudgetDisplayName(b)}
                      formatCurrency={formatCurrency}
                      onClick={openDetail}
                    />
                  ))}
                </div>
                <div className="pv-foot">
                  <button className="pv-see" onClick={openDetail}>
                    {t('budgetProgress.openDetail')} <ArrowRight />
                  </button>
                </div>
              </>
            ) : emptyBudgets
          )}
        </div>

        {isDetailOpen && (
          <div className="pv-sheet-ov" role="dialog" aria-modal="true">
            <div className="pv-sheet-bd" onClick={() => setIsDetailOpen(false)} />
            <div className="pv-sheet-wrap">
              <div className="pv-sheet">
                <div className="pv-sheet-head">
                  <h3 className="pv-title">{t('budgetProgress.title')}</h3>
                  <div className="segmented">
                    <button
                      onClick={() => setActiveTab('envelopes')}
                      className={activeTab === 'envelopes' ? 'on' : undefined}
                    >
                      {t('budgetProgress.envelopesTab')}
                    </button>
                    <button
                      onClick={() => setActiveTab('budgets')}
                      className={activeTab === 'budgets' ? 'on' : undefined}
                    >
                      {t('budgetProgress.expensesTab')}
                    </button>
                  </div>
                  <span className="pv-sheet-sp" />
                  {canCreate && (
                    <button
                      onClick={handleOpenConfig}
                      className="icon-btn"
                      style={{ color: 'var(--pa)', borderColor: 'var(--pa-soft)' }}
                      aria-label={activeTab === 'budgets' ? t('budgetProgressDetails.addBudget') : t('envelopeConfig.newEnvelope')}
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => setIsDetailOpen(false)}
                    className="icon-btn"
                    aria-label={t('budgetProgress.close')}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="pv-sheet-body">
                  {activeTab === 'envelopes' ? (
                    hasEnvelopes ? (
                      <>
                        <div className="pv-gsum two">
                          <div><div className="l">{t('envelopeProgress.savedThisMonth')}</div><div className="v">{formatCurrency(totalThisMonth)}</div></div>
                          <div><div className="l">{t('envelopeConfig.targetAmount')}</div><div className="v">{formatCurrency(totalTarget)}</div></div>
                        </div>
                        <div className="pv-items">
                          {filteredEnvelopes.map((e) => (
                            <PvEnvelopeItem
                              key={e.id}
                              envelope={e}
                              formatCurrency={formatCurrency}
                              viewMonth={month}
                              viewYear={year}
                              onEdit={handleEditEnvelope}
                              onConvert={handleConvertToSpending}
                              onComplete={handleCompleteEnvelope}
                              t={t}
                            />
                          ))}
                        </div>
                      </>
                    ) : emptyEnvelopes
                  ) : (
                    hasBudgets ? (
                      <>
                        <div className="pv-gsum">
                          <div><div className="l">{t('budgetProgressDetails.budgeted')}</div><div className="v">{formatCurrency(displayTotalBudgeted)}</div></div>
                          <div><div className="l">{t('budgetProgressDetails.spent')}</div><div className="v">{formatCurrency(displayTotalSpent)}</div></div>
                          <div><div className="l">{t('budgetProgressDetails.remaining')}</div><div className={clsx('v', displayTotalRemaining >= 0 ? 'pos' : 'neg')}>{formatCurrency(displayTotalRemaining)}</div></div>
                        </div>
                        <div className="pv-items">
                          {filteredBudgets.map((b) => (
                            <PvBudgetItem
                              key={b.id}
                              budget={b}
                              name={getBudgetDisplayName(b)}
                              formatCurrency={formatCurrency}
                              bankAccounts={bankAccounts}
                              onEdit={() => handleEditBudget(b)}
                              t={t}
                            />
                          ))}
                        </div>
                      </>
                    ) : emptyBudgets
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {configModals}
      </>
    );
  }

  return (
    <div className="card h-full">
      {carouselElement}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{t('budgetProgress.title')}</h3>
          <HelpTooltip helpKey="budgetProgress" />
          {/* Ordre aligné sur la modale : Enveloppe puis Budget */}
          <div className="segmented">
            <button
              onClick={() => setActiveTab('envelopes')}
              className={activeTab === 'envelopes' ? 'on' : undefined}
            >
              {t('budgetProgress.envelopesTab')}
            </button>
            <button
              onClick={() => setActiveTab('budgets')}
              className={activeTab === 'budgets' ? 'on' : undefined}
            >
              {t('budgetProgress.expensesTab')}
            </button>
          </div>
          {activeTab === 'envelopes' && (
            <HelpTooltip helpKey="envelopeLifecycle" />
          )}
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <button
              onClick={handleOpenConfig}
              className="icon-btn"
              style={{ color: 'var(--pa)', borderColor: 'var(--pa-soft)' }}
              title={activeTab === 'budgets' ? t('budgetProgressDetails.addBudget') : t('envelopeConfig.newEnvelope')}
            >
              <Plus className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {activeTab === 'budgets' ? (
        /* ===== ONGLET BUDGETS ===== */
        <>
          {!hasBudgets ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Target className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm mb-3">{t('budgetProgress.noBudgets')}</p>
              {canCreateBudget ? (
                <button
                  onClick={handleOpenConfig}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('budgetProgressDetails.createBudget')}
                </button>
              ) : (
                <UpgradePrompt compact />
              )}
            </div>
          ) : (
            <>
              {/* Résumé global */}
              <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">{t('budgetProgressDetails.budgeted')}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(displayTotalBudgeted)}</p>
                </div>
                <div className="text-center border-x border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">{t('budgetProgressDetails.spent')}</p>
                  <p className="text-sm font-semibold text-gray-900">{formatCurrency(displayTotalSpent)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">{t('budgetProgressDetails.remaining')}</p>
                  <p className={`text-sm font-semibold ${displayTotalRemaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(displayTotalRemaining)}
                  </p>
                </div>
              </div>

              {/* Liste des budgets */}
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {filteredBudgets.map((budget) => {
                  const hasWeeklyProgress = budget.weeklyProgress && budget.weeklyProgress.length > 0;
                  const isExpanded = expandedBudgets.has(budget.id);

                  return (
                    <div
                      key={budget.id}
                      className="bg-gray-50 rounded-lg overflow-hidden"
                    >
                      <button
                        onClick={() => handleEditBudget(budget)}
                        className="w-full text-left p-3 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            {getStatusIcon(budget.status)}
                            {budget.categoryIcon && (
                              <DynamicIcon name={budget.categoryIcon} className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            )}
                            <span className="font-medium text-gray-900 truncate">
                              {getBudgetDisplayName(budget)}
                            </span>
                            {hasWeeklyProgress && (
                              <span className="text-xs text-gray-400 ml-1">({t('budgetConfig.perWeek')})</span>
                            )}
                            {budget.bankAccountIds && budget.bankAccountIds.length > 0 && bankAccounts && bankAccounts.length > 0 && (
                              budget.bankAccountIds.map(id => {
                                const account = bankAccounts.find(a => a.id === id);
                                if (!account) return null;
                                return (
                                  <span
                                    key={id}
                                    className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs"
                                    style={{ backgroundColor: `${account.color}20`, color: account.color }}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: account.color }} />
                                    {account.name}
                                  </span>
                                );
                              })
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={clsx(
                              'text-sm font-medium',
                              budget.remaining >= 0 ? 'text-gray-700' : 'text-red-600'
                            )}>
                              {budget.remaining >= 0 ? formatCurrency(budget.remaining) : `-${formatCurrency(Math.abs(budget.remaining))}`}
                            </span>
                            {hasWeeklyProgress && (
                              <button
                                onClick={(e) => toggleBudgetExpand(budget.id, e)}
                                className="p-0.5 hover:bg-gray-200 rounded"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-500" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                          <div
                            className={clsx(
                              'h-full rounded-full transition-all duration-500',
                              getStatusColor(budget.status)
                            )}
                            style={{ width: `${Math.min(budget.progressPercent, 100)}%` }}
                          />
                        </div>

                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{formatCurrency(budget.spent)} {t('budgetProgress.spent')}</span>
                          <span>{budget.progressPercent.toFixed(0)}%</span>
                          <span>{formatCurrency(budget.amount)} {t('budgetProgressDetails.budgeted').toLowerCase()}</span>
                        </div>
                      </button>

                      {hasWeeklyProgress && isExpanded && (
                        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-gray-200">
                          {budget.weeklyProgress!.map((week) => (
                            <div key={week.week} className="pl-2 border-l-2 border-gray-300">
                              <div className="flex items-center justify-between text-xs mb-0.5">
                                <span className="text-gray-600 font-medium">{week.weekLabel}</span>
                                <span className={clsx(
                                  'font-medium',
                                  getStatusTextColor(week.status)
                                )}>
                                  {formatCurrency(week.spent)} / {formatCurrency(week.amount)}
                                </span>
                              </div>
                              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={clsx(
                                    'h-full rounded-full transition-all duration-300',
                                    getStatusColorLight(week.status)
                                  )}
                                  style={{ width: `${Math.min(week.progressPercent, 100)}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      ) : (
        /* ===== ONGLET ENVELOPPES ===== */
        <>
          {!hasEnvelopes ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Wallet className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm mb-3">{t('envelopeProgress.noEnvelopes')}</p>
              {canCreateEnvelope ? (
                <button
                  onClick={handleOpenConfig}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('envelopeProgress.configureEnvelopes')}
                </button>
              ) : (
                <UpgradePrompt compact />
              )}
            </div>
          ) : (
            <>
              {/* Résumé enveloppes */}
              {(() => {
                const totalThisMonth = filteredEnvelopes
                  .reduce((sum, e) => sum + (e.projectedThisMonth || 0), 0);
                const totalTarget = filteredEnvelopes
                  .reduce((sum, e) => sum + e.targetAmount, 0);
                return (
                  <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">
                        {t('envelopeProgress.savedThisMonth')}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(totalThisMonth)}</p>
                    </div>
                    <div className="text-center border-l border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">{t('envelopeConfig.targetAmount')}</p>
                      <p className="text-sm font-semibold text-gray-900">{formatCurrency(totalTarget)}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Liste des enveloppes */}
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {filteredEnvelopes.map((envelope) => (
                  <EnvelopeItem
                    key={envelope.id}
                    envelope={envelope}
                    formatCurrency={formatCurrency}
                    onEdit={handleEditEnvelope}
                    onConvert={handleConvertToSpending}
                    onComplete={handleCompleteEnvelope}
                    viewMonth={month}
                    viewYear={year}
                    t={t}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {configModals}
    </div>
  );
}

// ============================================
// Sous-composant pour une enveloppe
// ============================================

function EnvelopeItem({
  envelope,
  formatCurrency,
  onEdit,
  onConvert,
  onComplete,
  viewMonth,
  viewYear,
  t,
}: {
  envelope: EnvelopeWithProgress;
  formatCurrency: (amount: number) => string;
  onEdit: (e: EnvelopeWithProgress) => void;
  onConvert: (id: string, e: React.MouseEvent) => void;
  onComplete: (id: string, e: React.MouseEvent) => void;
  viewMonth?: number;
  viewYear?: number;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  // Déterminer le mode d'affichage selon le mois consulté vs targetDate
  const targetDate = new Date(envelope.targetDate);
  const targetMonthIndex = targetDate.getFullYear() * 12 + targetDate.getMonth();
  const now = new Date();
  const viewMonthIndex = (viewMonth && viewYear)
    ? viewYear * 12 + (viewMonth - 1)
    : now.getFullYear() * 12 + now.getMonth();

  const isFinished = envelope.status === 'spending' || envelope.status === 'completed';
  // Afficher en mode épargne si on consulte un mois AVANT la date cible
  const showSavingsView = viewMonthIndex < targetMonthIndex;
  // Afficher en mode dépense uniquement sur le mois de targetDate ou après
  const showSpendingView = !showSavingsView && isFinished;

  if (showSpendingView) {
    // Mode spending
    const isExceeded = envelope.spendingRemaining < 0;
    const progressPercent = Math.min(envelope.spendingProgressPercent, 100);

    return (
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <button
          onClick={() => onEdit(envelope)}
          className="w-full text-left p-3 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <Wallet className="w-4 h-4 text-violet-500 flex-shrink-0" />
              <span className="font-medium text-gray-900 truncate">{envelope.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 flex-shrink-0">
                {envelope.status === 'completed' ? t('envelopeProgress.completed') : t('envelopeProgress.spendingMode')}
              </span>
            </div>
            <span className={clsx(
              'text-sm font-medium flex-shrink-0 ml-2',
              isExceeded ? 'text-red-600' : 'text-gray-700'
            )}>
              {isExceeded
                ? `-${formatCurrency(Math.abs(envelope.spendingRemaining))}`
                : formatCurrency(envelope.spendingRemaining)
              }
            </span>
          </div>

          {/* Barre de progression spending */}
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
            <div
              className={clsx(
                'h-full rounded-full transition-all duration-500',
                isExceeded ? 'bg-red-500' : 'bg-violet-500'
              )}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="flex justify-between text-xs text-gray-500">
            <span>
              {formatCurrency(envelope.totalSpent)} {t('envelopeProgress.spent')}
            </span>
            <span>{envelope.spendingProgressPercent.toFixed(0)}%</span>
            <span>
              {formatCurrency(envelope.spendingBudget)} {t('envelopeProgress.saved')}
            </span>
          </div>

          {/* Info objectif initial si différent */}
          {envelope.targetAmount !== envelope.spendingBudget && (
            <div className="mt-1 text-xs text-gray-400">
              {t('envelopeProgress.originalTarget')} : {formatCurrency(envelope.targetAmount)}
            </div>
          )}
        </button>

        {/* Bouton Terminer (seulement si en spending, pas completed) */}
        {envelope.status === 'spending' && (
          <div className="px-3 pb-2 flex justify-end">
            <button
              onClick={(e) => onComplete(envelope.id, e)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            >
              <Check className="w-3 h-3" />
              {t('envelopeProgress.complete')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Mode épargne (active OU mois d'épargne d'une enveloppe spending/completed)
  const displayTotal = envelope.projectedTotal > 0 ? envelope.projectedTotal : envelope.totalContributed;
  const displayPercent = envelope.projectedTotal > 0 ? envelope.projectedProgressPercent : envelope.progressPercent;
  const hasRealContributions = envelope.totalContributed > 0;

  return (
    <div className="bg-gray-50 rounded-lg overflow-hidden">
      <button
        onClick={() => onEdit(envelope)}
        className="w-full text-left p-3 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <Wallet className={clsx('w-4 h-4 flex-shrink-0', envelope.atRisk ? 'text-amber-500' : 'text-emerald-500')} />
            <span className="font-medium text-gray-900 truncate">{envelope.name}</span>
            {/* Badge si l'enveloppe est terminée mais qu'on consulte un mois d'épargne */}
            {isFinished && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 flex-shrink-0">
                {envelope.status === 'completed' ? t('envelopeProgress.completed') : t('envelopeProgress.spendingMode')}
              </span>
            )}
            {!isFinished && envelope.atRisk && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0 flex items-center gap-0.5">
                <AlertTriangle className="w-3 h-3" />
                {t('envelopeProgress.atRisk')}
              </span>
            )}
            {!isFinished && !envelope.atRisk && envelope.currentMonthFulfilled && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                {t('envelopeProgress.monthFulfilled')}
              </span>
            )}
          </div>
          <span className="text-sm font-medium text-gray-700 flex-shrink-0 ml-2">
            {formatCurrency(envelope.recommendedContribution || 0)}/{t('envelopeProgress.perMonth')}
          </span>
        </div>

        {/* Barre de progression avec projection */}
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1 relative">
          {/* Barre de projection (fond) */}
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500 absolute inset-y-0 left-0',
              envelope.atRisk ? 'bg-amber-300' : 'bg-emerald-300'
            )}
            style={{ width: `${Math.min(displayPercent, 100)}%` }}
          />
          {/* Barre réelle au-dessus */}
          {hasRealContributions && (
            <div
              className="h-full rounded-full transition-all duration-500 bg-emerald-500 absolute inset-y-0 left-0"
              style={{ width: `${Math.min(envelope.progressPercent, 100)}%` }}
            />
          )}
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>
            {formatCurrency(displayTotal)}{' '}
            {hasRealContributions && displayTotal !== envelope.totalContributed
              ? t('envelopeProgress.projected')
              : t('envelopeProgress.saved')
            }
          </span>
          <span>{displayPercent.toFixed(0)}%</span>
          <span>
            {formatCurrency(envelope.targetAmount)} {t('budgetProgress.of')}
          </span>
        </div>

        {/* Info mois restants (monthsRemaining inclut le mois en cours, on affiche ceux APRÈS) */}
        <div className="mt-1 text-xs text-gray-400">
          {envelope.monthsRemaining > 1
            ? t('envelopeProgress.monthsRemaining', { count: envelope.monthsRemaining - 1 })
            : envelope.monthsRemaining === 1
              ? t('envelopeProgress.lastMonth')
              : t('envelopeProgress.targetReached')
          }
        </div>
        {!isFinished && envelope.atRisk && (
          <div className="mt-1 text-xs text-amber-600">
            {t('envelopeProgress.atRiskDetails')}
          </div>
        )}
      </button>

      {/* Bouton commencer à dépenser (seulement pour les enveloppes active) */}
      {!isFinished && (envelope.progressPercent >= 100 || envelope.monthsRemaining === 0) ? (
        <div className="px-3 pb-2 flex justify-end">
          <button
            onClick={(e) => onConvert(envelope.id, e)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-violet-600 bg-violet-100 hover:bg-violet-200 rounded-lg transition-colors"
          >
            <Play className="w-3 h-3" />
            {t('envelopeProgress.convertToSpending')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ============================================
// Vue Méridien — helpers sémantiques & sous-composants
// État porté par [data-state] : good (vert) / warn (ambre) / over (rouge)
// / spend (encre = phase dépense) / done (sourd = terminé).
// ============================================

type PvState = 'good' | 'warn' | 'over' | 'spend' | 'done';

function budgetPvState(status: BudgetWithProgress['status']): PvState {
  return status === 'exceeded' ? 'over' : status === 'warning' ? 'warn' : 'good';
}

interface EnvelopeResolved {
  mode: 'saving' | 'spending';
  isFinished: boolean;
  state: PvState;
  realPercent: number;
  projPercent: number | null;
  hasReal: boolean;
  displayTotal: number;
  displayPercent: number;
  isExceeded: boolean;
}

// Reproduit la logique de mode/état d'EnvelopeItem pour la vue Méridien
// (compact + détail), sans toucher au rendu legacy des autres thèmes.
function resolveEnvelope(e: EnvelopeWithProgress, viewMonth?: number, viewYear?: number): EnvelopeResolved {
  const targetDate = new Date(e.targetDate);
  const targetMonthIndex = targetDate.getFullYear() * 12 + targetDate.getMonth();
  const now = new Date();
  const viewMonthIndex = viewMonth && viewYear
    ? viewYear * 12 + (viewMonth - 1)
    : now.getFullYear() * 12 + now.getMonth();
  const isFinished = e.status === 'spending' || e.status === 'completed';
  const showSavingsView = viewMonthIndex < targetMonthIndex;
  const showSpendingView = !showSavingsView && isFinished;

  if (showSpendingView) {
    const isExceeded = e.spendingRemaining < 0;
    return {
      mode: 'spending',
      isFinished,
      state: isExceeded ? 'over' : e.status === 'completed' ? 'done' : 'spend',
      realPercent: Math.min(e.spendingProgressPercent, 100),
      projPercent: null,
      hasReal: true,
      displayTotal: e.totalSpent,
      displayPercent: e.spendingProgressPercent,
      isExceeded,
    };
  }

  const displayTotal = e.projectedTotal > 0 ? e.projectedTotal : e.totalContributed;
  const displayPercent = e.projectedTotal > 0 ? e.projectedProgressPercent : e.progressPercent;
  return {
    mode: 'saving',
    isFinished,
    state: e.atRisk ? 'warn' : 'good',
    realPercent: Math.min(e.progressPercent, 100),
    projPercent: Math.min(displayPercent, 100),
    hasReal: e.totalContributed > 0,
    displayTotal,
    displayPercent,
    isExceeded: false,
  };
}

function signedCurrency(amount: number, fmt: (n: number) => string): string {
  return amount >= 0 ? fmt(amount) : `-${fmt(Math.abs(amount))}`;
}

// ── Ligne compacte — budget ──
function PvBudgetRow({ budget, name, formatCurrency, onClick }: {
  budget: BudgetWithProgress;
  name: string;
  formatCurrency: (amount: number) => string;
  onClick: () => void;
}) {
  const state = budgetPvState(budget.status);
  return (
    <button type="button" className="pv-row" data-state={state} onClick={onClick}>
      <span className="pv-tile">
        {budget.categoryIcon ? <DynamicIcon name={budget.categoryIcon} /> : <Target />}
      </span>
      <span className="pv-mid">
        <span className="pv-name">
          {(state === 'warn' || state === 'over') && <span className="pv-flag" />}
          {name}
        </span>
        <span className="pv-mini"><i style={{ width: `${Math.min(budget.progressPercent, 100)}%` }} /></span>
      </span>
      <span className={clsx('pv-meta', budget.remaining < 0 && 'neg')}>
        <b>{signedCurrency(budget.remaining, formatCurrency)}</b>
        <small>{budget.progressPercent.toFixed(0)} %</small>
      </span>
    </button>
  );
}

// ── Ligne compacte — enveloppe ──
function PvEnvelopeRow({ envelope, formatCurrency, viewMonth, viewYear, onClick }: {
  envelope: EnvelopeWithProgress;
  formatCurrency: (amount: number) => string;
  viewMonth?: number;
  viewYear?: number;
  onClick: () => void;
}) {
  const r = resolveEnvelope(envelope, viewMonth, viewYear);
  const isSpending = r.mode === 'spending';
  const keyAmount = isSpending ? envelope.spendingRemaining : r.displayTotal;
  const pct = isSpending ? envelope.spendingProgressPercent : r.displayPercent;
  const isNeg = isSpending && r.isExceeded;
  return (
    <button type="button" className="pv-row" data-state={r.state} onClick={onClick}>
      <span className="pv-tile"><Wallet /></span>
      <span className="pv-mid">
        <span className="pv-name">
          {(r.state === 'warn' || r.state === 'over') && <span className="pv-flag" />}
          {envelope.name}
        </span>
        <span className="pv-mini">
          {r.projPercent != null && <i className="proj" style={{ width: `${r.projPercent}%` }} />}
          <i style={{ width: `${r.realPercent}%` }} />
        </span>
      </span>
      <span className={clsx('pv-meta', isNeg && 'neg')}>
        <b>{isNeg ? `-${formatCurrency(Math.abs(keyAmount))}` : formatCurrency(keyAmount)}</b>
        <small>{pct.toFixed(0)} %</small>
      </span>
    </button>
  );
}

// ── Item détail — budget ──
function PvBudgetItem({ budget, name, formatCurrency, bankAccounts, onEdit, t }: {
  budget: BudgetWithProgress;
  name: string;
  formatCurrency: (amount: number) => string;
  bankAccounts?: { id: string; name: string; color: string }[];
  onEdit: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const state = budgetPvState(budget.status);
  const hasWeekly = !!budget.weeklyProgress && budget.weeklyProgress.length > 0;
  return (
    <div
      className="pv-item"
      data-state={state}
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
    >
      <div className="pv-itop">
        <div className="pv-ilg">
          {budget.categoryIcon ? <DynamicIcon name={budget.categoryIcon} /> : <Target />}
        </div>
        <div className="pv-iid">
          <div className="pv-iname">
            <b>{name}</b>
            {hasWeekly && <span className="pv-chip muted">{t('budgetConfig.perWeek')}</span>}
            {budget.bankAccountIds && bankAccounts && budget.bankAccountIds.map((id) => {
              const account = bankAccounts.find((a) => a.id === id);
              if (!account) return null;
              return (
                <span
                  key={id}
                  className="pv-chip"
                  style={{ backgroundColor: `${account.color}22`, color: account.color }}
                >
                  <i style={{ width: 6, height: 6, borderRadius: '50%', background: account.color, display: 'inline-block' }} />
                  {account.name}
                </span>
              );
            })}
          </div>
        </div>
        <div className={clsx('pv-iamt', budget.remaining < 0 && 'neg')}>
          <div className="pv-iamt-v">
            <b>{signedCurrency(budget.remaining, formatCurrency)}</b>
            {budget.remaining >= 0 && <small>{t('budgetProgressDetails.remaining')}</small>}
          </div>
        </div>
      </div>
      <div className="pv-ibar"><i style={{ width: `${Math.min(budget.progressPercent, 100)}%` }} /></div>
      <div className="pv-ifoot">
        <span>{formatCurrency(budget.spent)} {t('budgetProgress.spent')}</span>
        <span className="pct">{budget.progressPercent.toFixed(0)} %</span>
        <span>{formatCurrency(budget.amount)} {t('budgetProgressDetails.budgeted').toLowerCase()}</span>
      </div>
      {hasWeekly && (
        <div className="pv-weeks">
          {budget.weeklyProgress!.map((week) => (
            <div key={week.week} className="pv-wk" data-state={budgetPvState(week.status)}>
              <div className="pv-wk-r">
                <span>{week.weekLabel}</span>
                <span>{formatCurrency(week.spent)} / {formatCurrency(week.amount)}</span>
              </div>
              <div className="pv-ibar"><i style={{ width: `${Math.min(week.progressPercent, 100)}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Item détail — enveloppe (modes épargne / dépense) ──
function PvEnvelopeItem({ envelope, formatCurrency, viewMonth, viewYear, onEdit, onConvert, onComplete, t }: {
  envelope: EnvelopeWithProgress;
  formatCurrency: (amount: number) => string;
  viewMonth?: number;
  viewYear?: number;
  onEdit: (e: EnvelopeWithProgress) => void;
  onConvert: (id: string, e: React.MouseEvent) => void;
  onComplete: (id: string, e: React.MouseEvent) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const r = resolveEnvelope(envelope, viewMonth, viewYear);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(envelope); }
  };

  if (r.mode === 'spending') {
    return (
      <div className="pv-item" data-state={r.state} role="button" tabIndex={0} onClick={() => onEdit(envelope)} onKeyDown={onKey}>
        <div className="pv-itop">
          <div className="pv-ilg"><Wallet /></div>
          <div className="pv-iid">
            <div className="pv-iname">
              <b>{envelope.name}</b>
              <span className={clsx('pv-chip', envelope.status === 'completed' && 'muted')}>
                {envelope.status === 'completed' ? t('envelopeProgress.completed') : t('envelopeProgress.spendingMode')}
              </span>
            </div>
            {envelope.targetAmount !== envelope.spendingBudget && (
              <div className="pv-isub">{t('envelopeProgress.originalTarget')} : {formatCurrency(envelope.targetAmount)}</div>
            )}
          </div>
          <div className={clsx('pv-iamt', r.isExceeded && 'neg')}>
            <div className="pv-iamt-v">
              <b>{r.isExceeded ? `-${formatCurrency(Math.abs(envelope.spendingRemaining))}` : formatCurrency(envelope.spendingRemaining)}</b>
              {!r.isExceeded && <small>{t('budgetProgressDetails.remaining')}</small>}
            </div>
          </div>
        </div>
        <div className="pv-ibar"><i style={{ width: `${r.realPercent}%` }} /></div>
        <div className="pv-ifoot">
          <span>{formatCurrency(envelope.totalSpent)} {t('envelopeProgress.spent')}</span>
          <span className="pct">{envelope.spendingProgressPercent.toFixed(0)} %</span>
          <span>{formatCurrency(envelope.spendingBudget)} {t('envelopeProgress.saved')}</span>
        </div>
        {envelope.status === 'spending' && (
          <div className="pv-iact">
            <button type="button" className="pv-btn ghost" onClick={(e) => onComplete(envelope.id, e)}>
              <Check />{t('envelopeProgress.complete')}
            </button>
          </div>
        )}
      </div>
    );
  }

  const canConvert = !r.isFinished && (envelope.progressPercent >= 100 || envelope.monthsRemaining === 0);
  return (
    <div className="pv-item" data-state={r.state} role="button" tabIndex={0} onClick={() => onEdit(envelope)} onKeyDown={onKey}>
      <div className="pv-itop">
        <div className="pv-ilg"><Wallet /></div>
        <div className="pv-iid">
          <div className="pv-iname">
            <b>{envelope.name}</b>
            {r.isFinished && (
              <span className="pv-chip muted">
                {envelope.status === 'completed' ? t('envelopeProgress.completed') : t('envelopeProgress.spendingMode')}
              </span>
            )}
            {!r.isFinished && envelope.atRisk && (
              <span className="pv-chip"><AlertTriangle />{t('envelopeProgress.atRisk')}</span>
            )}
            {!r.isFinished && !envelope.atRisk && envelope.currentMonthFulfilled && (
              <span className="pv-chip credit">{t('envelopeProgress.monthFulfilled')}</span>
            )}
          </div>
        </div>
        <div className="pv-iamt">
          <div className="pv-iamt-v">
            <b>{formatCurrency(envelope.recommendedContribution || 0)}</b>
            <small>/{t('envelopeProgress.perMonth')}</small>
          </div>
        </div>
      </div>
      <div className="pv-ibar">
        <i className="proj" style={{ width: `${r.projPercent ?? 0}%` }} />
        {r.hasReal && <i style={{ width: `${r.realPercent}%` }} />}
      </div>
      <div className="pv-ifoot">
        <span>
          {formatCurrency(r.displayTotal)}{' '}
          {r.hasReal && r.displayTotal !== envelope.totalContributed
            ? t('envelopeProgress.projected')
            : t('envelopeProgress.saved')}
        </span>
        <span className="pct">{r.displayPercent.toFixed(0)} %</span>
        <span>{formatCurrency(envelope.targetAmount)} {t('budgetProgress.of')}</span>
      </div>
      <div className="pv-inote">
        {envelope.monthsRemaining > 1
          ? t('envelopeProgress.monthsRemaining', { count: envelope.monthsRemaining - 1 })
          : envelope.monthsRemaining === 1
            ? t('envelopeProgress.lastMonth')
            : t('envelopeProgress.targetReached')}
      </div>
      {!r.isFinished && envelope.atRisk && (
        <div className="pv-inote warn">{t('envelopeProgress.atRiskDetails')}</div>
      )}
      {canConvert && (
        <div className="pv-iact">
          <button type="button" className="pv-btn primary" onClick={(e) => onConvert(envelope.id, e)}>
            <Play />{t('envelopeProgress.convertToSpending')}
          </button>
        </div>
      )}
    </div>
  );
}
